import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import Avatar from './Avatar';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import ImageUpload from './ImageUpload';
import '../styles/SubscriptionsPanel.css';
import '../styles/OrgBrandModal.css';
import '../styles/ManagementPanel.css';
import CustomSelect from './CustomSelect';
import CreateOrganizationModal from './CreateOrganizationModal';
import CreateBrandModal from './CreateBrandModal';

const OrganizationsBrandsPanel = ({ user, dashboardData, isSuperAdmin = false, onRefresh }) => {
  const modal = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('subsection') || 'view-orgs';
  const setActiveSection = (section) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('subsection', section);
      return next;
    });
  };

  // Get global org/brand context
  const { selectedOrganization } = useOrgBrand();

  // Modal states
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateBrandModal, setShowCreateBrandModal] = useState(false);

  // Subscription states for brand list badges
  const [brandSubscriptions, setBrandSubscriptions] = useState({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  // Edit states
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [editOrgForm, setEditOrgForm] = useState({
    organization_name: '',
    owner_id: '',
    icon_url: ''
  });
  const [initialOrgForm, setInitialOrgForm] = useState({
    organization_name: '',
    owner_id: '',
    icon_url: ''
  });
  const [editingBrand, setEditingBrand] = useState(null);
  const [editBrandForm, setEditBrandForm] = useState({
    brand_name: '',
    organization_id: '',
    owner_id: '',
    icon_url: '',
    green_light_mode: 'red',
    auto_approve_hours: 168  // 7 days default
  });
  const [initialBrandForm, setInitialBrandForm] = useState({
    brand_name: '',
    organization_id: '',
    owner_id: '',
    icon_url: '',
    green_light_mode: 'red',
    auto_approve_hours: 168
  });

  // Subscription state for Edit Brand Modal
  const [brandSubscription, setBrandSubscription] = useState({
    blog_post: { enabled: false, quantity: 0 },
    social_media: { enabled: false, quantity: 0, types: [] }
  });
  const [initialBrandSubscription, setInitialBrandSubscription] = useState({
    blog_post: { enabled: false, quantity: 0 },
    social_media: { enabled: false, quantity: 0, types: [] }
  });
  const [subscriptionConfig, setSubscriptionConfig] = useState({
    blog_quantities: [3, 5, 10],
    social_quantities: [5, 10, 15, 20],
    social_types: []
  });
  const [updatingSubscription, setUpdatingSubscription] = useState(false);

  // Organization selection state for sidebar layout
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Tracks an org awaiting selection once it shows up in the refreshed list
  // (e.g. right after creation, so Create Brand / Assign & Edit have an org to target)
  const [pendingSelectOrgId, setPendingSelectOrgId] = useState(null);

  // Assigned users accordion state - tracks which orgs have expanded assigned users
  const [expandedAssignedUsers, setExpandedAssignedUsers] = useState({});

  // Assignment dropdown state
  const [activeAssignmentDropdown, setActiveAssignmentDropdown] = useState(null); // { type: 'operator'|'client', context: 'org'|'brand', id: 'orgId' or 'brandId' }
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');

  // Get data - handle both super admin and admin data structures
  // Natural sort function for alphabetical and numerical sorting
  const naturalSort = (a, b, key) => {
    const aVal = String(a[key] || '').toLowerCase();
    const bVal = String(b[key] || '').toLowerCase();

    // Use localeCompare with numeric option for natural sorting
    return aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
  };

  // Get data and sort alphabetically/numerically
  const organizations = (dashboardData?.data?.organizations || dashboardData?.organizations || [])
    .slice()
    .sort((a, b) => naturalSort(a, b, 'organization_name') || naturalSort(a, b, 'name'));

  const brands = (dashboardData?.data?.brands || dashboardData?.brands || [])
    .slice()
    .sort((a, b) => naturalSort(a, b, 'brand_name') || naturalSort(a, b, 'name'));

  const clients = (dashboardData?.data?.clients || dashboardData?.clients || [])
    .slice()
    .sort((a, b) => naturalSort(a, b, 'name') || naturalSort(a, b, 'email'));

  const operators = (dashboardData?.data?.operators || dashboardData?.operators || [])
    .slice()
    .sort((a, b) => naturalSort(a, b, 'name') || naturalSort(a, b, 'email'));

  const admins = (dashboardData?.data?.admins || dashboardData?.admins || [])
    .slice()
    .sort((a, b) => naturalSort(a, b, 'name') || naturalSort(a, b, 'email'));

  // Sync global org context to local selectedOrg when viewing organizations
  // Only auto-select if no org is currently selected (QoL on initial view)
  useEffect(() => {
    if (activeSection === 'view-orgs' && selectedOrganization && !selectedOrg) {
      // Find the matching organization in the local list
      const orgInList = organizations.find(o =>
        (o._id || o.id) === (selectedOrganization._id || selectedOrganization.id)
      );
      if (orgInList) {
        setSelectedOrg(orgInList);
      }
    }
    // Reset selection when leaving view-orgs section
    if (activeSection !== 'view-orgs' && selectedOrg) {
      setSelectedOrg(null);
    }
  }, [activeSection, selectedOrganization, organizations, selectedOrg]);

  // Select a just-created organization once it lands in the refreshed list
  useEffect(() => {
    if (!pendingSelectOrgId) return;
    const newOrg = organizations.find(o => String(o._id || o.id) === String(pendingSelectOrgId));
    if (newOrg) {
      setSelectedOrg(newOrg);
      setPendingSelectOrgId(null);
    }
  }, [pendingSelectOrgId, organizations]);

  // Close assignment dropdown when clicking outside
  useEffect(() => {
    if (!activeAssignmentDropdown) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('.assignment-dropdown-wrapper')) {
        setActiveAssignmentDropdown(null);
        setAssignmentSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeAssignmentDropdown]);

  // Fetch brand subscriptions when organization is selected
  useEffect(() => {
    if (selectedOrg) {
      fetchBrandSubscriptions(selectedOrg._id);
    } else {
      setBrandSubscriptions({});
    }
  }, [selectedOrg]);

  const selectOrg = (org) => {
    setSelectedOrg(org);
  };

  const toggleAssignedUsers = (orgId) => {
    setExpandedAssignedUsers(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  // Handler for assigning users at organization level (access to ALL brands)
  const handleAssignToOrg = async (orgId, userType, userId) => {
    if (!userId) return;

    try {
      await axios.post(`${apiPrefix}/assign-to-organization`, {
        organization_id: orgId,
        user_id: userId,
        user_type: userType,
        brand_id: null // Null means org-level access to all brands
      }, { withCredentials: true });

      modal.success(`${userType === 'operator' ? 'Operator' : 'Client'} assigned successfully to organization`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error assigning to organization:', error);
      modal.error(error.response?.data?.error || 'Failed to assign user to organization');
    }
  };

  // Handler for assigning users at brand level (access ONLY to that brand)
  const handleAssignToBrand = async (brandId, orgId, userType, userId) => {
    if (!userId) return;

    try {
      await axios.post(`${apiPrefix}/assign-to-brand`, {
        organization_id: orgId,
        brand_id: brandId,
        user_id: userId,
        user_type: userType
      }, { withCredentials: true });

      modal.success(`${userType === 'operator' ? 'Operator' : 'Client'} assigned successfully to brand`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error assigning to brand:', error);
      modal.error(error.response?.data?.error || 'Failed to assign user to brand');
    }
  };

  // Handler for unassigning users from organization/brand
  const handleUnassignUser = async (userId, userType, userName, brandId = null, organizationId = null, isOrgLevel = true) => {
    const unassignMessage = brandId
      ? `Are you sure you want to remove ${userName} from this brand?`
      : organizationId
      ? (isOrgLevel
          ? `Are you sure you want to remove ${userName} from this organization? This will remove access to all brands.`
          : `Are you sure you want to remove ${userName} from all brands in this organization?`)
      : `Are you sure you want to completely unassign ${userName}?`;

    const result = await modal.confirm(unassignMessage, 'Confirm Unassignment');
    if (!result) {
      return;
    }

    try {
      const payload = {
        user_id: userId,
        user_type: userType
      };

      // If brandId is provided, we're removing from a specific brand only
      if (brandId) {
        payload.brand_id = brandId;
      } else if (organizationId) {
        payload.organization_id = organizationId;
      }

      await axios.post(`${apiPrefix}/unassign-user`, payload, { withCredentials: true });

      const successMessage = brandId
        ? `${userType === 'operator' ? 'Operator' : 'Client'} removed from brand successfully`
        : organizationId
        ? `${userType === 'operator' ? 'Operator' : 'Client'} removed successfully`
        : `${userType === 'operator' ? 'Operator' : 'Client'} unassigned successfully`;

      modal.success(successMessage);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error unassigning user:', error);
      modal.error(error.response?.data?.error || 'Failed to unassign user');
    }
  };

  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  // Helper function to get owner name from owner_id
  const getOwnerName = (ownerId) => {
    if (!ownerId) return 'Not assigned';
    const owner = clients.find(client => safeString(client._id || client.id) === safeString(ownerId));
    return owner ? safeString(owner.name || owner.full_name || owner.email) : 'Not assigned';
  };

  // API endpoint prefix - super admins use super-admin endpoints, admins use admin endpoints
  const apiPrefix = isSuperAdmin ? '/api/super-admin' : '/api/admin';

  const handleEditOrganization = (org) => {
    setEditingOrganization(org);
    const formState = {
      organization_name: org.organization_name || org.name || '',
      owner_id: org.owner_id || '',
      icon_url: org.icon_url || ''
    };
    setEditOrgForm(formState);
    setInitialOrgForm(formState);
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${apiPrefix}/organizations/${editingOrganization._id}`, editOrgForm);

      // Update selectedOrg with new values if it's the one being edited
      if (selectedOrg && selectedOrg._id === editingOrganization._id) {
        setSelectedOrg({
          ...selectedOrg,
          organization_name: editOrgForm.organization_name,
          owner_id: editOrgForm.owner_id || null,
          icon_url: editOrgForm.icon_url
        });
      }

      setEditingOrganization(null);
      setEditOrgForm({ organization_name: '', owner_id: '', icon_url: '' });
      modal.success('Organization updated successfully!');
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error updating organization:', error);
      modal.error(error.response?.data?.error || 'Failed to update organization');
    }
  };

  const handleDeleteOrganization = async (org) => {
    const orgName = org.organization_name || org.name;
    const result = await modal.confirm(`Are you sure you want to delete "${orgName}"?\n\nThis will also delete all brands under this organization.`, 'Delete Organization');
    if (!result) {
      return;
    }
    try {
      await axios.delete(`${apiPrefix}/organizations/${org._id}`);
      modal.success(`Organization "${orgName}" deleted successfully!`);
      setSelectedOrg(null); // Clear selection when org is deleted
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting organization:', error);
      modal.error(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  const handleDeleteBrand = async (brand) => {
    const brandName = brand.brand_name || brand.name;
    const result = await modal.confirm(`Are you sure you want to delete "${brandName}"?`, 'Delete Brand');
    if (!result) {
      return;
    }
    try {
      await axios.delete(`${apiPrefix}/brands/${brand._id}`);
      modal.success(`Brand "${brandName}" deleted successfully!`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting brand:', error);
      modal.error(error.response?.data?.error || 'Failed to delete brand');
    }
  };

  // Fetch subscription config
  const fetchSubscriptionConfig = async () => {
    try {
      const response = await axios.get('/api/subscriptions/config');
      setSubscriptionConfig(response.data);
    } catch (err) {
      console.error('Error fetching subscription config:', err);
    }
  };

  // Fetch subscription for a brand
  const fetchBrandSubscription = async (brandId) => {
    try {
      const response = await axios.get(`/api/subscriptions/brand/${brandId}`);
      const subscriptionData = response.data.plans || {
        blog_post: { enabled: false, quantity: 0 },
        social_media: { enabled: false, quantity: 0, types: [] }
      };
      setBrandSubscription(subscriptionData);
      setInitialBrandSubscription(JSON.parse(JSON.stringify(subscriptionData))); // Deep copy
    } catch (err) {
      console.error('Error fetching brand subscription:', err);
      const defaultSubscription = {
        blog_post: { enabled: false, quantity: 0 },
        social_media: { enabled: false, quantity: 0, types: [] }
      };
      setBrandSubscription(defaultSubscription);
      setInitialBrandSubscription(JSON.parse(JSON.stringify(defaultSubscription))); // Deep copy
    }
  };

  // Update blog subscription
  const updateBlogSubscription = async (brandId, enabled, quantity) => {
    setUpdatingSubscription(true);
    try {
      await axios.put(`/api/subscriptions/brand/${brandId}/blog`, {
        enabled,
        quantity
      });
      setBrandSubscription(prev => ({
        ...prev,
        blog_post: { enabled, quantity }
      }));
      modal.success('Blog subscription updated successfully');
    } catch (err) {
      console.error('Error updating blog subscription:', err);
      modal.error(err.response?.data?.error || 'Failed to update blog subscription');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  // Update social media subscription
  const updateSocialSubscription = async (brandId, enabled, quantity, types) => {
    setUpdatingSubscription(true);
    try {
      await axios.put(`/api/subscriptions/brand/${brandId}/social`, {
        enabled,
        quantity,
        types
      });
      setBrandSubscription(prev => ({
        ...prev,
        social_media: { enabled, quantity, types }
      }));
      modal.success('Social media subscription updated successfully');
    } catch (err) {
      console.error('Error updating social media subscription:', err);
      modal.error(err.response?.data?.error || 'Failed to update social media subscription');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  // Fetch subscriptions for all brands in selected organization
  const fetchBrandSubscriptions = async (orgId) => {
    setLoadingSubscriptions(true);
    try {
      const orgBrands = brands.filter(b =>
        safeString(b.organization_id) === safeString(orgId)
      );

      const subscriptionPromises = orgBrands.map(async (brand) => {
        try {
          const response = await axios.get(`/api/subscriptions/brand/${brand._id}`);
          return {
            brandId: brand._id,
            data: response.data.plans || {
              blog_post: { enabled: false, quantity: 0 },
              social_media: { enabled: false, quantity: 0, types: [] }
            }
          };
        } catch (err) {
          console.error(`Error fetching subscription for brand ${brand._id}:`, err);
          return {
            brandId: brand._id,
            data: {
              blog_post: { enabled: false, quantity: 0 },
              social_media: { enabled: false, quantity: 0, types: [] }
            }
          };
        }
      });

      const results = await Promise.all(subscriptionPromises);

      const subscriptionMap = {};
      results.forEach(({ brandId, data }) => {
        subscriptionMap[brandId] = data;
      });

      setBrandSubscriptions(subscriptionMap);
    } catch (error) {
      console.error('Error fetching brand subscriptions:', error);
      setBrandSubscriptions({});
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleEditBrand = async (brand) => {
    setEditingBrand(brand);
    const formState = {
      brand_name: brand.brand_name || brand.name || '',
      organization_id: brand.organization_id || '',
      owner_id: brand.owner_id || '',
      icon_url: brand.icon_url || '',
      green_light_mode: brand.green_light_mode || 'red',
      auto_approve_hours: brand.auto_approve_hours || 168
    };
    setEditBrandForm(formState);
    setInitialBrandForm(formState);
    // Fetch subscription data for this brand
    const brandId = brand._id || brand.id;
    await fetchSubscriptionConfig();
    await fetchBrandSubscription(brandId);
  };

  const hasOrgFormChanged = () => {
    return editOrgForm.organization_name !== initialOrgForm.organization_name ||
           editOrgForm.owner_id !== initialOrgForm.owner_id ||
           editOrgForm.icon_url !== initialOrgForm.icon_url;
  };

  const hasBrandFormChanged = () => {
    const formChanged = editBrandForm.brand_name !== initialBrandForm.brand_name ||
           editBrandForm.organization_id !== initialBrandForm.organization_id ||
           editBrandForm.owner_id !== initialBrandForm.owner_id ||
           editBrandForm.icon_url !== initialBrandForm.icon_url ||
           editBrandForm.green_light_mode !== initialBrandForm.green_light_mode ||
           editBrandForm.auto_approve_hours !== initialBrandForm.auto_approve_hours;

    const subscriptionChanged =
           brandSubscription.blog_post?.enabled !== initialBrandSubscription.blog_post?.enabled ||
           brandSubscription.blog_post?.quantity !== initialBrandSubscription.blog_post?.quantity ||
           brandSubscription.social_media?.enabled !== initialBrandSubscription.social_media?.enabled ||
           brandSubscription.social_media?.quantity !== initialBrandSubscription.social_media?.quantity ||
           JSON.stringify(brandSubscription.social_media?.types) !== JSON.stringify(initialBrandSubscription.social_media?.types);

    return formChanged || subscriptionChanged;
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    setUpdatingSubscription(true);
    try {
      // Update brand details
      await axios.put(`${apiPrefix}/brands/${editingBrand._id}`, editBrandForm);

      // Update subscriptions if changed
      const blogChanged = brandSubscription.blog_post?.enabled !== initialBrandSubscription.blog_post?.enabled ||
                          brandSubscription.blog_post?.quantity !== initialBrandSubscription.blog_post?.quantity;

      const socialChanged = brandSubscription.social_media?.enabled !== initialBrandSubscription.social_media?.enabled ||
                           brandSubscription.social_media?.quantity !== initialBrandSubscription.social_media?.quantity ||
                           JSON.stringify(brandSubscription.social_media?.types) !== JSON.stringify(initialBrandSubscription.social_media?.types);

      if (blogChanged) {
        await axios.put(`/api/subscriptions/brand/${editingBrand._id}/blog`, {
          enabled: brandSubscription.blog_post?.enabled,
          quantity: brandSubscription.blog_post?.quantity
        });
      }

      if (socialChanged) {
        await axios.put(`/api/subscriptions/brand/${editingBrand._id}/social`, {
          enabled: brandSubscription.social_media?.enabled,
          quantity: brandSubscription.social_media?.quantity,
          types: brandSubscription.social_media?.types || []
        });
      }

      setEditingBrand(null);
      setEditBrandForm({ brand_name: '', organization_id: '', owner_id: '', icon_url: '', green_light_mode: 'red', auto_approve_hours: 168 });
      modal.success('Brand updated successfully!');
      if (onRefresh) await onRefresh();
      if (selectedOrg) {
        await fetchBrandSubscriptions(selectedOrg._id);
      }
    } catch (error) {
      console.error('Error updating brand:', error);
      modal.error(error.response?.data?.error || 'Failed to update brand');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  // Get available users for assignment dropdown
  const getAvailableUsers = (type, context, id, orgId) => {
    const selectedOrgId = safeString(id);

    let availableUsers = [];
    if (type === 'operator') {
      // Combine operators and admins for operator assignment
      // Admins can be assigned as operators
      const operatorsAndAdmins = [...operators, ...admins];

      availableUsers = operatorsAndAdmins.filter(operator => {
        const opOrgIds = operator.organization_ids || [];
        const opBrandIds = operator.brand_ids || [];

        if (context === 'org') {
          return !opOrgIds.includes(selectedOrgId);
        } else {
          if (opOrgIds.includes(safeString(orgId))) return false;
          if (opBrandIds.includes(selectedOrgId)) return false;
          return true;
        }
      });
    } else {
      availableUsers = clients.filter(client => {
        const clientId = safeString(client._id || client.id);
        const clientOrgIds = client.organization_ids || [];
        const clientBrandIds = client.brand_ids || [];

        if (context === 'org') {
          if (selectedOrg.owner_id && safeString(selectedOrg.owner_id) === clientId) {
            return false;
          }
          return !clientOrgIds.includes(selectedOrgId);
        } else {
          const brand = brands.find(b => safeString(b._id) === selectedOrgId);
          if (selectedOrg.owner_id && safeString(selectedOrg.owner_id) === clientId) {
            return false;
          }
          if (brand && brand.owner_id && safeString(brand.owner_id) === clientId) {
            return false;
          }
          if (clientOrgIds.includes(safeString(orgId))) return false;
          if (clientBrandIds.includes(selectedOrgId)) return false;
          return true;
        }
      });
    }

    // Filter by search term
    if (assignmentSearchTerm.trim()) {
      const searchLower = assignmentSearchTerm.toLowerCase();
      availableUsers = availableUsers.filter(user => {
        const name = safeString(user.full_name || user.name || '').toLowerCase();
        const email = safeString(user.email).toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      });
    }

    // Sort by name
    return availableUsers.sort((a, b) => {
      const nameA = safeString(a.full_name || a.name || a.email).toLowerCase();
      const nameB = safeString(b.full_name || b.name || b.email).toLowerCase();
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Handle user selection from dropdown
  const handleUserSelect = (userId) => {
    if (!activeAssignmentDropdown) return;

    const { type, context, id, orgId } = activeAssignmentDropdown;

    if (context === 'org') {
      handleAssignToOrg(safeString(id), type, userId);
    } else {
      handleAssignToBrand(safeString(id), orgId, type, userId);
    }

    setActiveAssignmentDropdown(null);
    setAssignmentSearchTerm('');
  };

  // Toggle assignment dropdown
  const toggleAssignmentDropdown = (dropdownConfig) => {
    if (activeAssignmentDropdown &&
        activeAssignmentDropdown.type === dropdownConfig.type &&
        activeAssignmentDropdown.context === dropdownConfig.context &&
        activeAssignmentDropdown.id === dropdownConfig.id) {
      setActiveAssignmentDropdown(null);
      setAssignmentSearchTerm('');
    } else {
      setActiveAssignmentDropdown(dropdownConfig);
      setAssignmentSearchTerm('');
    }
  };

  // Whether a given assignment dropdown is currently open
  const isAssignmentDropdownActive = (dropdownConfig) => (
    activeAssignmentDropdown &&
    activeAssignmentDropdown.type === dropdownConfig.type &&
    activeAssignmentDropdown.context === dropdownConfig.context &&
    activeAssignmentDropdown.id === dropdownConfig.id
  );

  // Render inline user assignment dropdown
  const renderUserAssignmentDropdown = (dropdownConfig, buttonRef) => {
    const isActive = activeAssignmentDropdown &&
      activeAssignmentDropdown.type === dropdownConfig.type &&
      activeAssignmentDropdown.context === dropdownConfig.context &&
      activeAssignmentDropdown.id === dropdownConfig.id;

    if (!isActive) return null;

    const { type, context, id, orgId } = dropdownConfig;
    const availableUsers = getAvailableUsers(type, context, id, orgId);

    return (
      <div className="user-assignment-dropdown" ref={buttonRef}>
        <div className="user-assignment-search">
          <Icon name="search" size="1rem" />
          <input
            type="text"
            placeholder={`Search ${type === 'operator' ? 'operators' : 'clients'}...`}
            value={assignmentSearchTerm}
            onChange={(e) => setAssignmentSearchTerm(e.target.value)}
            autoFocus
          />
        </div>
        <div className="user-assignment-list">
          {availableUsers.length === 0 ? (
            <div className="no-users-available">
              {assignmentSearchTerm.trim() ? 'No matches found' : `No ${type === 'operator' ? 'operators' : 'clients'} available`}
            </div>
          ) : (
            availableUsers.map(user => (
              <div
                key={safeString(user._id || user.id)}
                className="user-assignment-item"
                onClick={() => handleUserSelect(safeString(user._id || user.id))}
              >
                <Avatar user={user} size="sm" fallbackType="icon" className="user-assignment-avatar" />
                <div className="user-assignment-info">
                  <div className="user-assignment-name">
                    {safeString(user.full_name || user.name || user.email)}
                  </div>
                  <div className="user-assignment-email">
                    {safeString(user.email)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Render subscription status badges for a brand
  const renderSubscriptionBadges = (brand) => {
    const brandId = safeString(brand._id);
    const subscription = brandSubscriptions[brandId];

    if (loadingSubscriptions) {
      return (
        <div className="brand-subscriptions-badges">
          <span className="subscription-badge loading">...</span>
        </div>
      );
    }

    const blogEnabled = subscription?.blog_post?.enabled || false;
    const socialEnabled = subscription?.social_media?.enabled || false;

    if (!blogEnabled && !socialEnabled) {
      return (
        <div className="brand-subscriptions-badges">
          <button
            type="button"
            className="subscription-badge add-subscription-badge"
            onClick={(e) => {
              e.stopPropagation();
              handleEditBrand(brand);
            }}
          >
            + Add Subscription
          </button>
        </div>
      );
    }

    return (
      <div className="brand-subscriptions-badges">
        {blogEnabled && (
          <span className="subscription-badge blog-post-badge active">
            Blog Post
          </span>
        )}
        {socialEnabled && (
          <span className="subscription-badge social-media-badge active">
            Social Media
          </span>
        )}
      </div>
    );
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'view-orgs':
      default:
        return (
          <div className="content-section">
            {organizations.length > 0 ? (
              <div className="orgs-brands-layout">
                {/* Left Sidebar - Organization List */}
                <div className="orgs-sidebar">
                  <h3>Organizations</h3>
                  <div className="orgs-list">
                    {organizations.map(org => {
                      const orgId = safeString(org._id);
                      const isSelected = selectedOrg && safeString(selectedOrg._id) === orgId;

                      return (
                        <div
                          key={orgId}
                          className={`org-sidebar-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => selectOrg(org)}
                        >
                          <div className="org-sidebar-header">
                            {org.icon_url ? (
                              <BrandIcon src={org.icon_url} alt={safeString(org.organization_name || org.name)} className="org-sidebar-logo" />
                            ) : (
                              <div className="org-sidebar-logo-placeholder">
                                <span>{safeString(org.organization_name || org.name).charAt(0).toUpperCase()}</span>
                              </div>
                            )}
                            <div className="org-sidebar-info">
                              <div className="org-sidebar-name">{safeString(org.organization_name || org.name)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="create-org-button"
                    data-onboarding="create-org-tab"
                    onClick={() => setShowCreateOrgModal(true)}
                  >
                    <span className="create-org-icon">
                      <Icon name="plus" size="1rem" />
                    </span>
                    New Organization
                  </button>
                </div>

                {/* Right Detail Panel - Organization Details */}
                <div className="orgs-detail-panel">
                  {!selectedOrg ? (
                    <div className="no-org-selected">
                      <h3>Select an Organization</h3>
                      <p>Choose an organization from the list to view and manage its details</p>
                    </div>
                  ) : (
                    <>
                      {/* Organization Header with Edit/Delete */}
                      <div className="org-detail-header">
                        <div className="org-detail-title-row">
                          {selectedOrg.icon_url ? (
                            <BrandIcon src={selectedOrg.icon_url} alt={safeString(selectedOrg.organization_name || selectedOrg.name)} className="org-detail-logo" />
                          ) : (
                            <div className="org-detail-logo-placeholder">
                              <span>{safeString(selectedOrg.organization_name || selectedOrg.name).charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          <div>
                            <h3>{safeString(selectedOrg.organization_name || selectedOrg.name)}</h3>
                            <span className="brand-owner">
                              Owner: {getOwnerName(selectedOrg.owner_id)}
                            </span>
                          </div>
                        </div>
                        <div className="org-detail-actions">
                          <button
                            className="button-main edit-btn"
                            onClick={() => handleEditOrganization(selectedOrg)}
                          >
                            Edit
                          </button>
                          <button
                            className="button-main delete-btn"
                            onClick={() => handleDeleteOrganization(selectedOrg)}
                          >
                            <Icon name="trash-empty" size="1rem" />
                          </button>
                        </div>
                      </div>

                      {/* Assign to Organization Section */}
                      <div className="org-detail-section assignment-section" data-onboarding="assign-edit-tab">
                        <h4>Assign to Organization</h4>
                        <div className="assignment-form">
                          <div className="form-row">
                            <div className="assignment-dropdown-wrapper">
                              <button
                                className={`assignment-button${isAssignmentDropdownActive({ type: 'operator', context: 'org', id: selectedOrg._id }) ? ' open' : ''}`}
                                onClick={() => toggleAssignmentDropdown({
                                  type: 'operator',
                                  context: 'org',
                                  id: selectedOrg._id
                                })}
                              >
                                <span className="assignment-button-label">Assign Operator</span>
                                <Icon name="chevron-down" size="0.9rem" className="assignment-button-arrow" />
                              </button>
                              {renderUserAssignmentDropdown({
                                type: 'operator',
                                context: 'org',
                                id: selectedOrg._id
                              })}
                            </div>

                            <div className="assignment-dropdown-wrapper">
                              <button
                                className={`assignment-button${isAssignmentDropdownActive({ type: 'client', context: 'org', id: selectedOrg._id }) ? ' open' : ''}`}
                                onClick={() => toggleAssignmentDropdown({
                                  type: 'client',
                                  context: 'org',
                                  id: selectedOrg._id
                                })}
                              >
                                <span className="assignment-button-label">Assign Client</span>
                                <Icon name="chevron-down" size="0.9rem" className="assignment-button-arrow" />
                              </button>
                              {renderUserAssignmentDropdown({
                                type: 'client',
                                context: 'org',
                                id: selectedOrg._id
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Assigned Users Section - Collapsible */}
                      <div className="org-detail-section">
                        <div
                          className="assigned-users-header"
                          onClick={() => toggleAssignedUsers(safeString(selectedOrg._id))}
                        >
                          <h4>
                            Assigned Users ({
                              (() => {
                                const selectedOrgId = safeString(selectedOrg._id);
                                const orgBrandIds = brands
                                  .filter(b => safeString(b.organization_id) === selectedOrgId)
                                  .map(b => safeString(b._id));

                                // Count both operators and admins
                                const operatorsAndAdminsForCount = [...operators, ...admins];
                                const operatorCount = operatorsAndAdminsForCount.filter(operator => {
                                  const opOrgIds = operator.organization_ids || [];
                                  const opBrandIds = operator.brand_ids || [];

                                  return opOrgIds.includes(selectedOrgId) ||
                                         opBrandIds.some(bid => orgBrandIds.includes(bid));
                                }).length;

                                const assignedClients = clients.filter(client => {
                                  const clientOrgIds = client.organization_ids || [];
                                  const clientBrandIds = client.brand_ids || [];

                                  return clientOrgIds.includes(selectedOrgId) ||
                                         clientBrandIds.some(bid => orgBrandIds.includes(bid));
                                });

                                const hasOwner = selectedOrg.owner_id && clients.some(c =>
                                  safeString(c._id || c.id) === safeString(selectedOrg.owner_id)
                                );
                                const ownerIsAlreadyCounted = hasOwner && assignedClients.some(c =>
                                  safeString(c._id || c.id) === safeString(selectedOrg.owner_id)
                                );
                                const clientCount = assignedClients.length + (hasOwner && !ownerIsAlreadyCounted ? 1 : 0);

                                return operatorCount + clientCount;
                              })()
                            })
                          </h4>
                          <span className="brand-expand-icon">
                            <Icon name={expandedAssignedUsers[safeString(selectedOrg._id)] ? 'chevron-down' : 'chevron-right'} size="0.8rem" />
                          </span>
                        </div>

                        {expandedAssignedUsers[safeString(selectedOrg._id)] && (
                          <div className="assigned-users-list">
                            {(() => {
                              const selectedOrgId = safeString(selectedOrg._id);
                              const orgBrands = brands.filter(b => safeString(b.organization_id) === selectedOrgId);
                              const orgBrandIds = orgBrands.map(b => safeString(b._id));

                              const renderBrandIcons = (brandList) => (
                                <div className="assigned-user-brand-icons">
                                  {brandList.map(brand => (
                                    brand.icon_url ? (
                                      <BrandIcon
                                        key={safeString(brand._id)}
                                        src={brand.icon_url}
                                        alt={safeString(brand.brand_name || brand.name)}
                                        title={safeString(brand.brand_name || brand.name)}
                                        className="assigned-user-brand-icon"
                                      />
                                    ) : (
                                      <div
                                        key={safeString(brand._id)}
                                        className="assigned-user-brand-icon-placeholder"
                                        title={safeString(brand.brand_name || brand.name)}
                                      >
                                        {safeString(brand.brand_name || brand.name).charAt(0).toUpperCase()}
                                      </div>
                                    )
                                  ))}
                                </div>
                              );

                              // Include both operators and admins who are assigned
                              const operatorsAndAdmins = [...operators, ...admins];
                              const filteredOperators = operatorsAndAdmins.filter(operator => {
                                const opOrgIds = operator.organization_ids || [];
                                const opBrandIds = operator.brand_ids || [];

                                return opOrgIds.includes(selectedOrgId) ||
                                       opBrandIds.some(bid => orgBrandIds.includes(bid));
                              });

                              const operatorCards = filteredOperators.map(operator => {
                                const opOrgIds = operator.organization_ids || [];
                                const isOrgLevel = opOrgIds.includes(selectedOrgId);
                                const opBrandIds = (operator.brand_ids || []).filter(bid => orgBrandIds.includes(bid));
                                const displayBrands = isOrgLevel
                                  ? orgBrands
                                  : opBrandIds.map(bid => brands.find(b => safeString(b._id) === bid)).filter(Boolean);

                                return (
                                  <div key={safeString(operator._id)} className="assigned-user-card">
                                    <div className="assigned-user-top">
                                      <Avatar user={operator} size="sm" fallbackType="icon" className="user-assignment-avatar" />
                                      <div className="assigned-user-details">
                                        <div className="assigned-user-name-line">
                                          <span className="assigned-user-name">{safeString(operator.full_name || operator.name || 'Unknown User')}</span>
                                          <span className="assigned-user-role role-operator">Op</span>
                                        </div>
                                        <span className="assigned-user-email">{safeString(operator.email)}</span>
                                      </div>
                                      <button
                                        className="assigned-user-remove-btn"
                                        title={isOrgLevel ? 'Remove from Organization' : 'Remove from All Brands'}
                                        onClick={() => handleUnassignUser(
                                          safeString(operator._id),
                                          'operator',
                                          safeString(operator.full_name || operator.email),
                                          null,
                                          selectedOrgId,
                                          isOrgLevel
                                        )}
                                      >
                                        <Icon name="cancel" size="1.25rem" />
                                      </button>
                                    </div>
                                    {displayBrands.length > 0 && renderBrandIcons(displayBrands)}
                                  </div>
                                );
                              });

                              let filteredClients = clients.filter(client => {
                                const clientOrgIds = client.organization_ids || [];
                                const clientBrandIds = client.brand_ids || [];

                                return clientOrgIds.includes(selectedOrgId) ||
                                       clientBrandIds.some(bid => orgBrandIds.includes(bid));
                              });

                              if (selectedOrg.owner_id) {
                                const owner = clients.find(c => safeString(c._id || c.id) === safeString(selectedOrg.owner_id));
                                const ownerAlreadyListed = filteredClients.some(c => safeString(c._id || c.id) === safeString(selectedOrg.owner_id));
                                if (owner && !ownerAlreadyListed) {
                                  filteredClients = [owner, ...filteredClients];
                                }
                              }

                              orgBrands.forEach(brand => {
                                if (brand.owner_id) {
                                  const brandOwner = clients.find(c => safeString(c._id || c.id) === safeString(brand.owner_id));
                                  const alreadyListed = filteredClients.some(c => safeString(c._id || c.id) === safeString(brand.owner_id));
                                  if (brandOwner && !alreadyListed) {
                                    filteredClients.push(brandOwner);
                                  }
                                }
                              });

                              const clientCards = filteredClients.map(client => {
                                const clientOrgIds = client.organization_ids || [];
                                const isOrgLevel = clientOrgIds.includes(selectedOrgId);
                                const clientBrandIds = (client.brand_ids || []).filter(bid => orgBrandIds.includes(bid));
                                const clientId = safeString(client._id || client.id);

                                const isOrgOwner = selectedOrg.owner_id && clientId === safeString(selectedOrg.owner_id);
                                const ownedBrands = orgBrands.filter(b =>
                                  b.owner_id && safeString(b.owner_id) === clientId
                                );

                                const displayBrands = isOrgLevel || isOrgOwner
                                  ? orgBrands
                                  : clientBrandIds.length > 0
                                    ? clientBrandIds.map(bid => brands.find(b => safeString(b._id) === bid)).filter(Boolean)
                                    : ownedBrands;

                                const roleLabel = isOrgOwner ? 'Owner' : (ownedBrands.length > 0 ? 'Brand Owner' : 'Client');
                                const roleClass = (isOrgOwner || ownedBrands.length > 0) ? 'role-owner' : 'role-client';

                                const shouldShowButton = isOrgOwner || ownedBrands.length > 0 || isOrgLevel || clientBrandIds.length > 0;

                                let buttonText = 'Remove from Organization';
                                if (isOrgOwner && !isOrgLevel && clientBrandIds.length === 0) {
                                  buttonText = 'Remove Org Owner';
                                } else if (!isOrgOwner && ownedBrands.length > 0 && !isOrgLevel && clientBrandIds.length === 0) {
                                  buttonText = 'Remove Brand Owner';
                                } else if (!isOrgOwner && !isOrgLevel && clientBrandIds.length > 0) {
                                  buttonText = 'Remove All Brands';
                                }

                                const handleRemoveClick = async () => {
                                  if (isOrgOwner) {
                                    const result = await modal.confirm(
                                      `${safeString(client.full_name || client.email)} is the Organization Owner.\n\n` +
                                      `Remove them from this organization? This will clear their ownership and remove all assignments.`,
                                      'Confirm Removal'
                                    );
                                    if (!result) return;

                                    try {
                                      await axios.put(`${apiPrefix}/organizations/${selectedOrg._id}`, {
                                        organization_name: selectedOrg.organization_name || selectedOrg.name,
                                        owner_id: '',
                                        icon_url: selectedOrg.icon_url || ''
                                      }, { withCredentials: true });

                                      setSelectedOrg({
                                        ...selectedOrg,
                                        owner_id: null
                                      });

                                      if (isOrgLevel || clientBrandIds.length > 0) {
                                        await handleUnassignUser(
                                          clientId,
                                          'client',
                                          safeString(client.full_name || client.email),
                                          null,
                                          selectedOrgId,
                                          isOrgLevel
                                        );
                                      } else {
                                        modal.success('Organization owner removed successfully');
                                        if (onRefresh) await onRefresh();
                                      }
                                    } catch (error) {
                                      console.error('Error removing org owner:', error);
                                      modal.error('Failed to remove organization owner');
                                    }
                                  } else if (ownedBrands.length > 0 && !isOrgLevel && clientBrandIds.length === 0) {
                                    const brandNames = ownedBrands.map(b => safeString(b.brand_name || b.name)).join(', ');
                                    const result = await modal.confirm(
                                      `${safeString(client.full_name || client.email)} is the owner of: ${brandNames}\n\n` +
                                      `Remove their brand ownership?`,
                                      'Confirm Removal'
                                    );
                                    if (!result) return;

                                    try {
                                      for (const brand of ownedBrands) {
                                        await axios.put(`${apiPrefix}/brands/${brand._id}`, {
                                          brand_name: brand.brand_name || brand.name,
                                          organization_id: brand.organization_id,
                                          owner_id: '',
                                          icon_url: brand.icon_url || '',
                                          green_light_mode: brand.green_light_mode || 'red',
                                          auto_approve_hours: brand.auto_approve_hours || 168
                                        }, { withCredentials: true });
                                      }
                                      modal.success('Brand ownership removed successfully');
                                      if (onRefresh) await onRefresh();
                                    } catch (error) {
                                      console.error('Error removing brand ownership:', error);
                                      modal.error('Failed to remove brand ownership');
                                    }
                                  } else {
                                    await handleUnassignUser(
                                      clientId,
                                      'client',
                                      safeString(client.full_name || client.email),
                                      null,
                                      selectedOrgId,
                                      isOrgLevel
                                    );
                                  }
                                };

                                return (
                                  <div key={clientId} className="assigned-user-card">
                                    <div className="assigned-user-top">
                                      <Avatar user={client} size="sm" fallbackType="icon" className="user-assignment-avatar" />
                                      <div className="assigned-user-details">
                                        <div className="assigned-user-name-line">
                                          <span className="assigned-user-name">{safeString(client.full_name || client.name || 'Unknown User')}</span>
                                          <span className={`assigned-user-role ${roleClass}`}>{roleLabel}</span>
                                        </div>
                                        <span className="assigned-user-email">{safeString(client.email)}</span>
                                      </div>
                                      {shouldShowButton && (
                                        <button
                                          className="assigned-user-remove-btn"
                                          title={buttonText}
                                          onClick={handleRemoveClick}
                                        >
                                          <Icon name="cancel" size="1.25rem" />
                                        </button>
                                      )}
                                    </div>
                                    {displayBrands.length > 0 && renderBrandIcons(displayBrands)}
                                  </div>
                                );
                              });

                              if (operatorCards.length === 0 && clientCards.length === 0) {
                                return <p className="no-assignments">No users assigned</p>;
                              }

                              return <>{operatorCards}{clientCards}</>;
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Brands Section */}
                      <div className="org-detail-section">

                        <div className="brands-table-header">
                          <h4 className="brands-table-header-brands">Brands ({
                            brands.filter(brand =>
                              safeString(brand.organization_id) === safeString(selectedOrg._id)
                            ).length || 0
                          })</h4>
                          <span className="brands-table-header-subs">Subscriptions</span>
                          <span className="brands-table-header-actions"></span>
                        </div>
                        <div className="brands-detail-list">
                          {brands
                            .filter(brand => safeString(brand.organization_id) === safeString(selectedOrg._id))
                            .sort((a, b) => {
                              const nameA = (a.brand_name || a.name || '').toLowerCase();
                              const nameB = (b.brand_name || b.name || '').toLowerCase();
                              return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
                            })
                            .map(brand => {
                              const brandId = safeString(brand._id);
                              return (
                                <div key={brandId} className="brand-item-wrapper">
                                  <div className="brand-item" onClick={() => handleEditBrand(brand)}>
                                    <div className="brand-info-section">
                                      {brand.icon_url ? (
                                        <BrandIcon src={brand.icon_url} alt={safeString(brand.brand_name || brand.name)} className="brand-logo" />
                                      ) : (
                                        <div className="brand-logo-placeholder">
                                          <span>{safeString(brand.brand_name || brand.name).charAt(0).toUpperCase()}</span>
                                        </div>
                                      )}
                                      <div className="brand-text-info">
                                        <span className="brand-name">{safeString(brand.brand_name || brand.name)}</span>
                                      </div>
                                    </div>
                                    {renderSubscriptionBadges(brand)}
                                    <div className="brand-actions">
                                      <button
                                        className="button-main edit-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditBrand(brand);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="button-main delete-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteBrand(brand);
                                        }}
                                      >
                                        <Icon name="trash-empty" size="1rem" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          }
                          {!brands.some(brand =>
                            safeString(brand.organization_id) === safeString(selectedOrg._id)
                          ) && (
                            <p className="no-brands">No brands in this organization</p>
                          )}
                          <button
                            className="create-brand-button"
                            data-onboarding="create-brand-tab"
                            onClick={() => {
                              const brandsInOrg = brands.filter(brand =>
                                safeString(brand.organization_id) === safeString(selectedOrg._id)
                              );
                              if (brandsInOrg.length >= 3) {
                                modal.error('To add more Brands, create a new Organization.\nThis Organization already has 3 of 3 Brands.');
                              } else {
                                setShowCreateBrandModal(true);
                              }
                            }}
                          >
                            <span className="create-brand-icon">
                              <Icon name="plus" size="1rem" />
                            </span>
                            New Brand
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="button-main">
                <p>No organizations found.</p>
                <button
                  className="button-main"
                  data-onboarding="create-org-tab"
                  onClick={() => setShowCreateOrgModal(true)}
                >
                  Create Your First Organization
                </button>
              </div>
            )}

            {/* Edit Organization Modal */}
            {editingOrganization && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Organization</h3>
                    <button className="modal-close-btn" onClick={() => setEditingOrganization(null)} aria-label="Close" />
                  </div>
                  <form onSubmit={handleUpdateOrganization}>
                    <div className="form-group">
                      <label>Organization Name *</label>
                      <input
                        type="text"
                        value={editOrgForm.organization_name}
                        onChange={(e) => setEditOrgForm({...editOrgForm, organization_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Owner (Optional)</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <CustomSelect
                          value={editOrgForm.owner_id}
                          onChange={(e) => setEditOrgForm({...editOrgForm, owner_id: e.target.value})}
                        >
                          <option value="" disabled hidden>Select Owner</option>
                          {clients.map(client => (
                            <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                              {safeString(client.name)} ({safeString(client.email)})
                            </option>
                          ))}
                        </CustomSelect>
                        {editOrgForm.owner_id && String(editOrgForm.owner_id).trim() !== '' && (
                          <button
                            type="button"
                            onClick={() => setEditOrgForm({...editOrgForm, owner_id: ''})}
                            style={{
                              backgroundColor: 'var(--color-danger-button)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              width: '32px',
                              height: '32px',
                              cursor: 'pointer',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                            title="Remove owner"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <ImageUpload
                        value={editOrgForm.icon_url}
                        onChange={(base64) => setEditOrgForm({...editOrgForm, icon_url: base64})}
                        onClear={() => setEditOrgForm({...editOrgForm, icon_url: ''})}
                        label="Organization Icon"
                        required={false}
                        maxSizeMB={5}
                        fileType="organization_icon"
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="button-main" disabled={!hasOrgFormChanged()}>Save Changes</button>
                      <button type="button" className="button-main" onClick={() => setEditingOrganization(null)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Brand Modal */}
            {editingBrand && (
              <div className="modal-overlay">
                <div className="modal-content brand-edit-modal">
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Brand</h3>
                    <button className="modal-close-btn" onClick={() => setEditingBrand(null)} aria-label="Close" />
                  </div>
                  <form onSubmit={handleUpdateBrand}>
                    <div className="brand-modal-columns">
                      <div className="brand-modal-left">
                        <div className="brand-modal-icon-upload">
                          <ImageUpload
                            value={editBrandForm.icon_url}
                            onChange={(base64) => setEditBrandForm({...editBrandForm, icon_url: base64})}
                            onClear={() => setEditBrandForm({...editBrandForm, icon_url: ''})}
                            label="Brand Icon"
                            required={false}
                            maxSizeMB={5}
                            fileType="brand_icon"
                          />
                        </div>
                        <div className="form-group">
                          <label>Approval Mode</label>
                          <CustomSelect
                              value={editBrandForm.green_light_mode}
                              onChange={(e) => setEditBrandForm({...editBrandForm, green_light_mode: e.target.value})}
                            >
                              <option value="red"><span className="approval-dot red"></span>Red</option>
                              <option value="yellow"><span className="approval-dot yellow"></span>Yellow</option>
                              <option value="green"><span className="approval-dot green"></span>Green</option>
                            </CustomSelect>
                          {editBrandForm.green_light_mode === 'red' && (
                            <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary-on-light)' }}>
                              Manual approval required before content is sent to the client
                            </small>
                          )}
                          {editBrandForm.green_light_mode === 'yellow' && (
                            <div style={{ marginTop: '10px' }}>
                              <label>Auto-approve after (days)</label>
                              <input
                                type="number"
                                min="1"
                                max="30"
                                value={Math.round(editBrandForm.auto_approve_hours / 24)}
                                onChange={(e) => setEditBrandForm({...editBrandForm, auto_approve_hours: (parseInt(e.target.value) || 7) * 24})}
                              />
                              <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary-on-light)' }}>
                                Items will be automatically approved after {Math.round(editBrandForm.auto_approve_hours / 24)} days if not manually reviewed
                              </small>
                            </div>
                          )}
                          {editBrandForm.green_light_mode === 'green' && (
                            <small style={{ display: 'block', marginTop: '5px', color: 'var(--color-text-secondary-on-light)' }}>
                              Items will be automatically approved immediately when sent for client approval
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="brand-modal-right">
                        <div className="form-group">
                          <label>Brand Name *</label>
                          <input
                            type="text"
                            value={editBrandForm.brand_name}
                            onChange={(e) => setEditBrandForm({...editBrandForm, brand_name: e.target.value})}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Parent Organization *</label>
                          <CustomSelect
                              value={editBrandForm.organization_id}
                              onChange={(e) => setEditBrandForm({...editBrandForm, organization_id: e.target.value})}
                              required
                            >
                              <option value="">Select Organization</option>
                              {organizations.map(org => (
                                <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                                  {safeString(org.organization_name || org.name)}
                                </option>
                              ))}
                            </CustomSelect>
                        </div>
                        <div className="form-group">
                          <label>Owner (Optional)</label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <CustomSelect
                              value={editBrandForm.owner_id}
                              onChange={(e) => setEditBrandForm({...editBrandForm, owner_id: e.target.value})}
                            >
                              <option value="" disabled hidden>Select Owner</option>
                              {clients.map(client => (
                                <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                                  {safeString(client.name)} ({safeString(client.email)})
                                </option>
                              ))}
                            </CustomSelect>
                            {editBrandForm.owner_id && String(editBrandForm.owner_id).trim() !== '' && (
                              <button
                                type="button"
                                className="brand-owner-remove-btn"
                                onClick={() => setEditBrandForm({...editBrandForm, owner_id: ''})}
                                title="Remove owner"
                              >
                                <Icon name="cancel" size="1.1rem" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Assign to Brand Section */}
                        <div className="brand-assignment-controls">
                          <label className="brand-assignment-heading">Assign Users</label>
                          <div className="brand-assignment-row">
                            <div className="assignment-dropdown-wrapper">
                              <button
                                type="button"
                                className={`assignment-button${isAssignmentDropdownActive({ type: 'operator', context: 'brand', id: safeString(editingBrand._id) }) ? ' open' : ''}`}
                                onClick={() => toggleAssignmentDropdown({
                                  type: 'operator',
                                  context: 'brand',
                                  id: safeString(editingBrand._id),
                                  orgId: editBrandForm.organization_id
                                })}
                              >
                                <span className="assignment-button-label">Assign Operator</span>
                                <Icon name="chevron-down" size="0.9rem" className="assignment-button-arrow" />
                              </button>
                              {renderUserAssignmentDropdown({
                                type: 'operator',
                                context: 'brand',
                                id: safeString(editingBrand._id),
                                orgId: editBrandForm.organization_id
                              })}
                            </div>

                            <div className="assignment-dropdown-wrapper">
                              <button
                                type="button"
                                className={`assignment-button${isAssignmentDropdownActive({ type: 'client', context: 'brand', id: safeString(editingBrand._id) }) ? ' open' : ''}`}
                                onClick={() => toggleAssignmentDropdown({
                                  type: 'client',
                                  context: 'brand',
                                  id: safeString(editingBrand._id),
                                  orgId: editBrandForm.organization_id
                                })}
                              >
                                <span className="assignment-button-label">Assign Client</span>
                                <Icon name="chevron-down" size="0.9rem" className="assignment-button-arrow" />
                              </button>
                              {renderUserAssignmentDropdown({
                                type: 'client',
                                context: 'brand',
                                id: safeString(editingBrand._id),
                                orgId: editBrandForm.organization_id
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Subscriptions Section */}
                        <div className="form-group">
                          <label>Service Subscriptions</label>
                          <table className="brand-subscriptions-table">
                            <thead>
                              <tr>
                                <th>Service</th>
                                <th>Activate</th>
                                <th>Meeting Notifications</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Blog Post Workflow</td>
                                <td>
                                  <div
                                    className={`toggle-switch ${brandSubscription.blog_post?.enabled ? 'enabled' : ''} ${updatingSubscription ? 'toggling' : ''}`}
                                    onClick={() => !updatingSubscription && setBrandSubscription(prev => ({
                                      ...prev,
                                      blog_post: {
                                        ...prev.blog_post,
                                        enabled: !prev.blog_post?.enabled,
                                        quantity: !prev.blog_post?.enabled ? (prev.blog_post?.quantity || 3) : 0
                                      }
                                    }))}
                                  >
                                    <div className="toggle-knob" />
                                  </div>
                                </td>
                                <td></td>
                              </tr>
                              {brandSubscription.blog_post?.enabled && (
                                <tr className="subscription-quantity-row">
                                  <td colSpan={3}>
                                    <label style={{ fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>Posts per month:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="10"
                                      value={brandSubscription.blog_post?.quantity || 3}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value >= 1 && value <= 10) {
                                          setBrandSubscription(prev => ({
                                            ...prev,
                                            blog_post: { ...prev.blog_post, quantity: value }
                                          }));
                                        }
                                      }}
                                      disabled={updatingSubscription}
                                    />
                                    <small className="plan-note" style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--color-text-tertiary-on-light)' }}>
                                      AI will generate {(brandSubscription.blog_post?.quantity || 3) * 2} blog ideas for review
                                    </small>
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td>Social Media Workflow</td>
                                <td>
                                  <div
                                    className={`toggle-switch ${brandSubscription.social_media?.enabled ? 'enabled' : ''} ${updatingSubscription ? 'toggling' : ''}`}
                                    onClick={() => !updatingSubscription && setBrandSubscription(prev => ({
                                      ...prev,
                                      social_media: {
                                        ...prev.social_media,
                                        enabled: !prev.social_media?.enabled,
                                        quantity: !prev.social_media?.enabled ? (prev.social_media?.quantity || 5) : 0,
                                        types: prev.social_media?.types || []
                                      }
                                    }))}
                                  >
                                    <div className="toggle-knob" />
                                  </div>
                                </td>
                                <td></td>
                              </tr>
                              {brandSubscription.social_media?.enabled && (
                                <tr className="subscription-quantity-row">
                                  <td colSpan={3}>
                                    <label style={{ fontSize: '0.85rem', marginBottom: '0.5rem', display: 'block' }}>Posts per month:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="20"
                                      value={brandSubscription.social_media?.quantity || 5}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value >= 1 && value <= 20) {
                                          setBrandSubscription(prev => ({
                                            ...prev,
                                            social_media: { ...prev.social_media, quantity: value }
                                          }));
                                        }
                                      }}
                                      disabled={updatingSubscription}
                                    />
                                    <small className="plan-note" style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--color-text-tertiary-on-light)' }}>
                                      AI will generate {(brandSubscription.social_media?.quantity || 5) * 2} social media ideas for review
                                    </small>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="form-actions brand-modal-actions">
                      <button type="submit" className="brand-modal-confirm-btn" disabled={!hasBrandFormChanged()}>Confirm</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="super-admin-internal-layout">
      <div className="internal-content">
        {renderSectionContent()}
      </div>

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateOrgModal(false)}
          onSuccess={async (newOrgId) => {
            if (onRefresh) await onRefresh();
            if (newOrgId) setPendingSelectOrgId(newOrgId);
          }}
          clients={clients}
          apiPrefix={apiPrefix}
        />
      )}

      {/* Create Brand Modal */}
      {showCreateBrandModal && (
        <CreateBrandModal
          onClose={() => setShowCreateBrandModal(false)}
          onSuccess={onRefresh}
          organizations={organizations}
          organization={selectedOrg}
          brands={brands}
          clients={clients}
          apiPrefix={apiPrefix}
        />
      )}
    </div>
  );
};

export default OrganizationsBrandsPanel;
